import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Tv, 
  Zap, 
  Droplets, 
  Trash, 
  Utensils, 
  Car, 
  Shield, 
  Wine,
  Wrench,
  MoreHorizontal,
  Play,
  RefreshCw,
  Building2,
  Pencil
} from "lucide-react";

const EXPENSE_CATEGORIES = [
  { value: "cable", label: "Cable/Internet", icon: Tv, color: "#10B981" },
  { value: "electric", label: "Electric", icon: Zap, color: "#F59E0B" },
  { value: "water", label: "Water", icon: Droplets, color: "#3B82F6" },
  { value: "garbage", label: "Garbage", icon: Trash, color: "#8B5CF6" },
  { value: "food", label: "Food/Groceries", icon: Utensils, color: "#EC4899" },
  { value: "car_payment", label: "Car Payment", icon: Car, color: "#EF4444" },
  { value: "insurance", label: "Insurance", icon: Shield, color: "#14B8A6" },
  { value: "night_out", label: "Night Out", icon: Wine, color: "#F97316" },
  { value: "maintenance", label: "Maintenance", icon: Wrench, color: "#6366F1" },
  { value: "subscription", label: "Subscriptions", icon: Play, color: "#E11D48" },
  { value: "other", label: "Other", icon: MoreHorizontal, color: "#6B7280" },
];

const POPULAR_SUBSCRIPTIONS = [
  "Netflix", "Amazon Prime", "HBO Max", "Disney+", "Hulu", 
  "Spotify", "Apple Music", "YouTube Premium", "Apple TV+",
  "Paramount+", "Peacock", "ESPN+", "Adobe Creative Cloud",
  "Microsoft 365", "iCloud", "Dropbox", "Google One"
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

export default function Expenses() {
  const { api } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterProperty, setFilterProperty] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    category: "",
    description: "",
    date: new Date().toISOString().split('T')[0],
    property_id: "",
    is_recurring: false,
    recurring_frequency: "monthly"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expRes, propRes] = await Promise.all([
        api.get("/expenses"),
        api.get("/properties")
      ]);
      setExpenses(expRes.data);
      setProperties(propRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const res = await api.get("/expenses");
      setExpenses(res.data);
    } catch (error) {
      toast.error("Failed to fetch expenses");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        property_id: formData.property_id === "none" ? null : (formData.property_id || null),
        is_recurring: formData.is_recurring,
        recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null
      };

      if (editingExpense) {
        // Update existing expense
        await api.put(`/expenses/${editingExpense.id}`, payload);
        toast.success("Expense updated successfully");
      } else {
        // Create new expense
        await api.post("/expenses", payload);
        toast.success("Expense added successfully");
      }
      
      setDialogOpen(false);
      setEditingExpense(null);
      setFormData({
        name: "",
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString().split('T')[0],
        property_id: "",
        is_recurring: false,
        recurring_frequency: "monthly"
      });
      await fetchExpenses();
    } catch (error) {
      console.error("Expense error:", error);
      toast.error(error.response?.data?.detail || "Failed to save expense");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name || "",
      amount: expense.amount?.toString() || "",
      category: expense.category || "",
      description: expense.description || "",
      date: expense.date || new Date().toISOString().split('T')[0],
      property_id: expense.property_id || "",
      is_recurring: expense.is_recurring || false,
      recurring_frequency: expense.recurring_frequency || "monthly"
    });
    setDialogOpen(true);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingExpense(null);
      setFormData({
        name: "",
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString().split('T')[0],
        property_id: "",
        is_recurring: false,
        recurring_frequency: "monthly"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      fetchExpenses();
    } catch (error) {
      toast.error("Failed to delete expense");
    }
  };

  // Get property name by ID
  const getPropertyName = (propertyId) => {
    if (!propertyId) return null;
    const prop = properties.find(p => p.id === propertyId);
    return prop ? prop.name : null;
  };

  // Filter expenses by property
  const filteredExpenses = expenses.filter(exp => {
    if (filterProperty === "all") return true;
    if (filterProperty === "personal") return !exp.property_id;
    return exp.property_id === filterProperty;
  });

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const expensesByCategory = filteredExpenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + exp.amount;
    return acc;
  }, {});

  return (
    <Layout title="Expenses">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-expenses-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total Expenses</p>
                <p className="text-2xl font-bold text-red-500 font-mono">{formatCurrency(totalExpenses)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Property Filter */}
          <div className="flex items-center gap-2">
            <Select
              value={filterProperty}
              onValueChange={setFilterProperty}
            >
              <SelectTrigger className="bg-black/50 border-white/10 rounded-sm w-48" data-testid="filter-property-select">
                <SelectValue placeholder="Filter by property" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All Expenses</SelectItem>
                <SelectItem value="personal">Personal Only</SelectItem>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                data-testid="add-expense-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  {editingExpense ? "Edit Expense" : "Add New Expense"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Monthly Electric Bill"
                    required
                    data-testid="expense-name-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="expense-amount-input"
                    className="bg-black/50 border-white/10 rounded-sm font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="expense-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {EXPENSE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Property Selector */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Property (Optional)</Label>
                  <Select
                    value={formData.property_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, property_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="expense-property-select">
                      <SelectValue placeholder="Select property (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="none">No Property (Personal Expense)</SelectItem>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.name} - {prop.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">Link this expense to a property for tracking</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="expense-date-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description (Optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Additional notes"
                    data-testid="expense-description-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                  data-testid="submit-expense-btn"
                >
                  Add Expense
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {EXPENSE_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const amount = expensesByCategory[cat.value] || 0;
            return (
              <Card 
                key={cat.value} 
                className="bg-[#0A0A0A] border-white/10 rounded-none card-hover"
                data-testid={`expense-category-${cat.value}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{cat.label}</p>
                  </div>
                  <p className="text-lg font-bold text-white font-mono">
                    {formatCurrency(amount)}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Expenses list */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="expenses-list">
          <CardHeader>
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
              Expense History
              {filterProperty !== "all" && (
                <span className="text-sm text-zinc-500 ml-2">
                  ({filterProperty === "personal" ? "Personal" : getPropertyName(filterProperty)})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-500">Loading...</div>
            ) : filteredExpenses.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No expenses recorded yet. Add your first expense!
              </div>
            ) : (
              <div className="space-y-2">
                {filteredExpenses.map((expense) => {
                  const category = EXPENSE_CATEGORIES.find(c => c.value === expense.category);
                  const Icon = category?.icon || MoreHorizontal;
                  const propertyName = getPropertyName(expense.property_id);
                  return (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between p-4 border border-white/5 hover:border-white/10 transition-colors"
                      data-testid={`expense-item-${expense.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-10 h-10 rounded flex items-center justify-center"
                          style={{ backgroundColor: `${category?.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: category?.color }} />
                        </div>
                        <div>
                          <p className="font-medium text-white">{expense.name}</p>
                          <p className="text-sm text-zinc-500">
                            {category?.label || expense.category}
                            {propertyName && (
                              <span className="text-teal-400 ml-2">• {propertyName}</span>
                            )}
                            {expense.description && ` • ${expense.description}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-500 font-mono">
                            -{formatCurrency(expense.amount)}
                          </p>
                          <p className="text-xs text-zinc-500">{expense.date}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(expense)}
                          className="text-zinc-500 hover:text-blue-500 hover:bg-blue-500/10"
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(expense.id)}
                          className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
